import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import { Civility } from 'core/enums/civility';
import Agency from 'core/models/agency';
import { GET, POST, PUT } from 'utils/http';

// Couche d'accès « état serveur » des agences via TanStack Query.
// Le cache remplace l'ancien flag Redux `alreadyFetched`
// (agenciesAlreadyFetchedSelector -> !useAgenciesQuery().isLoading),
// et l'invalidation de cette clé remplace les actions SetAgencies/AddAgency/UpdateAgency + refetch.
export const agenciesQueryKey = ['agencies'] as const;

// GET utilisé par l'ancien AgenciesProvider (WithAgencies > agenciesFetcher).
export const fetchAgencies = async (): Promise<Agency[]> => {
    const response = (await GET(`${BACKEND_URL}/api/agencies`)) as Agency[];

    return response ?? [];
};

// Hook de liste de base (équivalent agenciesSelector via .data).
export const useAgenciesQuery = () =>
    useQuery({
        queryKey: agenciesQueryKey,
        queryFn: fetchAgencies,
    });

// agencyByIdSelector(state, id) -> useAgencyById(id)
// Conserve la transformation exacte (find par id) et reste mémoïsée via `select`.
export const useAgencyById = (id: string | undefined) =>
    useQuery({
        queryKey: agenciesQueryKey,
        queryFn: fetchAgencies,
        select: (agencies: Agency[]) => agencies.find((agency) => agency.id === id),
        enabled: Boolean(id),
    });

// À appeler après une mutation pour resynchroniser la liste depuis le serveur.
export const useInvalidateAgencies = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: agenciesQueryKey });
};

// --- Mutations (remplacent les write actions AddAgency / UpdateAgency) ---

type AgencyUserProfileInputs = {
    civility: Civility;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    mobileNumber: string;
    job: string;
};

export type CreateAgencyInputs = {
    name: string;
    email: string;
    userProfile: AgencyUserProfileInputs;
};

export type UpdateAgencyInputs = CreateAgencyInputs;

interface UpdateAgencyResponse {
    agency: Agency;
}

export type AllocateLicensesInputs = {
    agencyId: string;
    numLicensesToAllocate: number;
};

// AddAgency : POST /api/agency (créé une agence puis ajoute à la liste).
export const useCreateAgencyMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (inputs: CreateAgencyInputs) =>
            POST<Agency>(`${BACKEND_URL}/api/agency`, inputs) as Promise<Agency>,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: agenciesQueryKey }),
    });
};

// UpdateAgency : PUT /api/agency/:id (met à jour une agence existante).
export const useUpdateAgencyMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, inputs }: { id: string; inputs: UpdateAgencyInputs }) => {
            const response = (await PUT<UpdateAgencyResponse>(`${BACKEND_URL}/api/agency/${id}`, {
                ...inputs,
            })) as UpdateAgencyResponse;

            return response.agency;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: agenciesQueryKey }),
    });
};

// UpdateAgency (via LicenseForm) : POST /api/agency/:id/allocate-licenses (renvoie l'agence mise à jour).
export const useAllocateLicensesMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, inputs }: { id: string; inputs: AllocateLicensesInputs }) =>
            POST<Agency>(`${BACKEND_URL}/api/agency/${id}/allocate-licenses`, inputs) as Promise<Agency>,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: agenciesQueryKey }),
    });
};
